/**
 * Dashboard Nav › Route, Nearby and Map (spec §2.2 tab 5; W4b card C3). Route shows the plotted route with Clear
 * route; Nearby lists known stations nearest first with service tags, a service filter and **Set course** per row
 * (`setCourseTo`); Map is a launcher: it closes the dashboard and opens the full-screen system or galaxy map, and
 * closing the map returns here (the maps feature's return hook).
 */
import { useState, type ReactElement } from 'react'
import { FocusButton, FocusContext } from '../../../focus'
import type { FocusGroup } from '../../../nav'
import type { ReachCommandResult } from '../../../../reach/types'
import { loadDashboardConfig } from '../../config'
import type { StationDirectory } from '../../data'
import type { DashContext, DashSection, DashSectionProps } from '../../registry'
import { useDashFocus } from '../../useDashFocus'
import { useOpenRefresh } from '../jobs/live'
import { fill } from '../jobs/model'
import { loadNavText, NEARBY_FILTERS, nearbyModel, routeModel, type NearbyFilter, type RouteInput } from './model'

export interface NavSectionDeps {
  route(): RouteInput | null
  dir(): StationDirectory | null
  clearRoute(): ReachCommandResult
  setCourse(stationId: string): ReachCommandResult
  /** Closes the dashboard and opens the system map (galaxy: focused on this system); closing it returns to Nav › Map. */
  openMap(kind: 'system' | 'galaxy'): void
}

const careerOnly = (ctx: DashContext): boolean => ctx.career

function RouteSection(deps: NavSectionDeps, p: DashSectionProps): ReactElement {
  useOpenRefresh()
  const t = loadNavText()
  const input = deps.route()
  const m = input === null ? null : routeModel(input, t)
  const focus = useDashFocus(p.nav, [{ id: 'list', count: 1 }])
  return (
    <FocusContext.Provider value={focus}>
      <div className="dash-nav" data-dash-nav="route">
        {m === null || !m.plotted ? (
          // The shell's empty state; the ETA and any blocker (a mass lock) sit under its line, not far below it.
          <div className="dash-empty" data-route-plotted="false">
            <p className="dash-empty__line">{t.route.none}</p>
            {m !== null && m.eta !== null ? <p className="dash-empty__why">{m.eta}</p> : null}
            {m?.blockers.map((b) => (
              <p key={b} className="dash-empty__why dash-warn">
                {b}
              </p>
            ))}
          </div>
        ) : (
          <>
            <p className="dash-row__title" data-route-plotted="true">
              <strong>{m.to}</strong> · {m.left}
            </p>
            {m.next !== null ? <p className="dash-row__meta">{m.next}</p> : null}
            {m.eta !== null ? <p className="dash-row__meta">{m.eta}</p> : null}
            {m.blockers.map((b) => (
              <p key={b} className="dash-row__meta dash-warn">
                {b}
              </p>
            ))}
          </>
        )}
        {m?.plotted === true ? (
          <FocusButton group="list" index={0} testId="dash-route-clear" onActivate={() => deps.clearRoute()}>
            {t.route.clear}
          </FocusButton>
        ) : null}
      </div>
    </FocusContext.Provider>
  )
}

function NearbySection(deps: NavSectionDeps, p: DashSectionProps): ReactElement {
  useOpenRefresh()
  const t = loadNavText()
  const words = loadDashboardConfig().dockedAction
  const [filter, setFilter] = useState<NearbyFilter>('all')
  const [line, setLine] = useState('')
  const dir = deps.dir()
  const rows = dir === null ? [] : nearbyModel(dir, filter, words, t)
  const groups: FocusGroup[] = [
    { id: 'filter', count: NEARBY_FILTERS.length, cols: NEARBY_FILTERS.length, down: 'list' },
    { id: 'list', count: Math.max(1, rows.length), up: 'filter' },
  ]
  const focus = useDashFocus(p.nav, groups)
  const course = (id: string, name: string): void => {
    const r = deps.setCourse(id)
    setLine(r.ok ? fill(t.nearby.courseSet, { target: name }) : r.message)
  }
  return (
    <FocusContext.Provider value={focus}>
      <div className="dash-nav" data-dash-nav="nearby">
        <div className="dash-filter" role="toolbar">
          {NEARBY_FILTERS.map((f, i) => (
            <FocusButton key={f} group="filter" index={i} className={f === filter ? 'is-active' : undefined} onActivate={() => setFilter(f)}>
              {f === 'all' ? t.nearby.all : t.nearby.services[f]}
            </FocusButton>
          ))}
        </div>
        {rows.length === 0 ? <p className="dash-empty">{t.nearby.empty}</p> : null}
        <ul className="dash-list">
          {rows.map((r, i) => (
            <li key={r.id} className="dash-row" data-station={r.id}>
              <p className="dash-row__title">
                <strong>{r.name}</strong> · {r.where}
              </p>
              <p className="dash-row__meta">{r.services.join(' · ')}</p>
              <FocusButton group="list" index={i} testId={`dash-nearby-course-${i}`} onActivate={() => course(r.id, r.name)}>
                <kbd>F</kbd> {words.setCourse}
              </FocusButton>
            </li>
          ))}
        </ul>
        {line !== '' ? (
          <p className="dash-row__meta" data-dash-course-result="">
            {line}
          </p>
        ) : null}
      </div>
    </FocusContext.Provider>
  )
}

function MapSection(deps: NavSectionDeps, p: DashSectionProps): ReactElement {
  const t = loadNavText()
  const focus = useDashFocus(p.nav, [{ id: 'list', count: 2 }])
  return (
    <FocusContext.Provider value={focus}>
      <div className="dash-nav" data-dash-nav="map">
        <FocusButton group="list" index={0} testId="dash-map-system" onActivate={() => deps.openMap('system')}>
          {t.map.system} <kbd>{t.map.systemKey}</kbd>
        </FocusButton>
        <FocusButton group="list" index={1} testId="dash-map-galaxy" onActivate={() => deps.openMap('galaxy')}>
          {t.map.galaxy}
        </FocusButton>
        <p className="dash-row__meta">{t.map.note}</p>
      </div>
    </FocusContext.Provider>
  )
}

/** The three Nav sections, in order (ids `nav.route`, `nav.nearby`, `nav.map`). */
export function createNavSections(deps: NavSectionDeps): DashSection[] {
  const t = loadNavText()
  const Route = (p: DashSectionProps): ReactElement => RouteSection(deps, p)
  const Nearby = (p: DashSectionProps): ReactElement => NearbySection(deps, p)
  const Launcher = (p: DashSectionProps): ReactElement => MapSection(deps, p)
  return [
    { id: 'nav.route', tab: 'nav', order: 10, label: t.sections.route, available: careerOnly, component: Route },
    { id: 'nav.nearby', tab: 'nav', order: 20, label: t.sections.nearby, available: careerOnly, component: Nearby },
    { id: 'nav.map', tab: 'nav', order: 30, label: t.sections.map, available: careerOnly, component: Launcher },
  ]
}
